/**
 * SSML → 音声の合成を担うフック (計画 9.5.3 / M5-3)。
 *
 * Step3 の候補カードに載せる音声を「候補ごとに」管理する。
 *
 *   - 合成      … `POST /audio-preview` (Amazon Polly / 話者 Takumi 固定)
 *   - キャッシュ … **キーは SSML 文字列**。同一 SSML の再合成をスキップする
 *   - 解放      … 差し替え時に旧 URL を revoke / アンマウント時に全件 revoke
 *   - 直列実行  … 複数件を同時に投げず 1 件ずつ合成する
 *
 * ## なぜフックに切り出すか
 *
 * Blob URL の解放漏れは **画面上では気づけない** (メモリが増えるだけで
 * 見た目も動作も変わらない)。解放を 1 箇所へ集約し、
 * 「差し替え時」「クリア時」「アンマウント時」の 3 経路をここで閉じる
 * (計画リスク R10 / R20)。候補カード側に散らすと必ずどこかが漏れる。
 *
 * ## ⚠️ 直列 (1 件ずつ) にする理由
 *
 * 候補数ぶんの Polly 呼び出しを `Promise.all` で並列にすると同時実行数が
 * 跳ね上がり、スロットリングで **後半の候補だけ毎回失敗する**
 * (計画 9.7 の R15)。既存の放送地点プレビュー
 * (`MultilingualPreviewButton`) も順次合成しており、流儀を揃えている。
 *
 * ## ⚠️ 本フックがしないこと
 *
 * 再生の制御 (`play()`) は行わない。候補が複数並ぶ画面で自動再生すると
 * 複数の音声が同時に鳴るため、再生は利用者の操作に委ねる
 * (`<audio controls>` を出すだけ)。
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { base64ToAudioUrl } from '../../api/broadcastPoints';

import { SSML_TUNING_LANGUAGE_KEY, SSML_TUNING_VOICE_ID } from './constants';
import { describeApiErrorMessage } from '../../utils/apiErrorMessage';

/**
 * 候補 1 件ぶんの音声の状態 (計画 9.3.2)。
 *
 * `pending` と `synthesizing` は **画面表示上は同じ**「音声を生成中…」だが、
 * 内部では分けておく (`synthesizing` を二重呼び出しの防止に使う)。
 */
export type SsmlAudioStatus = 'pending' | 'synthesizing' | 'ready' | 'error';

export interface SsmlAudioState {
  /** 現在の状態 */
  status: SsmlAudioStatus;
  /** 再生・ダウンロードに使う blob: URL (`ready` のときのみ) */
  url: string | null;
  /** 失敗理由 (`error` のときのみ)。サーバのメッセージをそのまま出す */
  error: string | null;
}

/** 合成に使う関数の型 (`BroadcastPointsClient.previewAudio` を注入する)。 */
export type PreviewAudioFn = (payload: {
  ssml: string;
  voiceId: string;
  language: string;
}) => Promise<{ content_base64: string; content_type: string }>;

export interface UseSsmlAudioResult {
  /** 候補キー → 音声の状態 */
  audioStates: Record<string, SsmlAudioState>;
  /**
   * 候補 1 件を合成する (失敗しても **例外を投げない**)。
   *
   * 呼び出し側 (一括生成のループ) が 1 件の失敗で止まらないようにするため、
   * エラーは状態 (`status: 'error'`) として保持する。
   *
   * @returns 合成できたら `true` / 失敗したら `false`
   */
  synthesize: (key: string, ssml: string) => Promise<boolean>;
  /** 候補を `pending` (合成待ち) として登録する (カードを先に描画するため) */
  markPending: (keys: readonly string[]) => void;
  /** 保持している音声をすべて破棄し、blob URL を revoke する */
  reset: () => void;
}

/**
 * 候補ごとの音声を合成・保持する。
 *
 * @param previewAudio 音声合成関数 (テストで差し替えられるよう注入で受ける)
 */
export function useSsmlAudio(previewAudio: PreviewAudioFn): UseSsmlAudioResult {
  const [audioStates, setAudioStates] = useState<Record<string, SsmlAudioState>>({});

  /*
   * 合成済み SSML → blob URL のキャッシュ。
   *
   * ⚠️ キーを候補キーではなく **SSML 文字列** にするのが要点。
   * 同じ SSML なら音声も同じなので、再合成は無駄な Polly 課金になる
   * (計画リスク R4)。state ではなく ref に持つのは、
   * 合成中の連続呼び出しでも最新のキャッシュを参照できるようにするため。
   */
  const urlBySsmlRef = useRef<Map<string, string>>(new Map());

  /*
   * 直列化のための「直前の合成の完了を待つ Promise」。
   *
   * ⚠️ 呼び出し側が `for` で `await` していても、別経路 (再試行ボタン等) から
   * 同時に呼ばれる可能性がある。ここで鎖を作っておけば
   * **どの経路から呼ばれても 1 件ずつ** になる (R15)。
   */
  const queueRef = useRef<Promise<void>>(Promise.resolve());

  /** アンマウント時に全件 revoke するため、生成した URL を追跡する。 */
  const createdUrlsRef = useRef<Set<string>>(new Set());
  /**
   * マウント中かどうか。
   *
   * ⚠️ 合成中にアンマウントされた場合 (画面の「リセット」で Step を作り直した等)、
   * 応答が後から届くと **解放済みの追跡集合へ URL が追加され、誰も revoke しない**。
   * アンマウント後に届いた音声はその場で revoke して捨てるために持つ。
   */
  const mountedRef = useRef(true);
  useEffect(() => {
    // StrictMode の effect 二重実行 (cleanup → 再実行) でも true に戻す。
    mountedRef.current = true;
    // Set 自体は再代入しない (中身だけ増減する) ため、参照を取っておけば最新の URL を解放できる。
    const createdUrls = createdUrlsRef.current;
    return () => {
      mountedRef.current = false;
      for (const url of createdUrls) {
        URL.revokeObjectURL(url);
      }
      createdUrls.clear();
    };
  }, []);

  const reset = useCallback(() => {
    // ⚠️ クリア時に revoke しないと、再実行を繰り返すたびに URL が積み上がる (R20)。
    for (const url of createdUrlsRef.current) {
      URL.revokeObjectURL(url);
    }
    createdUrlsRef.current.clear();
    urlBySsmlRef.current.clear();
    setAudioStates({});
  }, []);

  const markPending = useCallback((keys: readonly string[]) => {
    setAudioStates((prev) => {
      const next = { ...prev };
      for (const key of keys) {
        next[key] = { status: 'pending', url: null, error: null };
      }
      return next;
    });
  }, []);

  const synthesize = useCallback(
    async (key: string, ssml: string): Promise<boolean> => {
      // 直前の合成が終わるまで待ってから自分の合成を始める (直列化 / R15)。
      const runAfter = queueRef.current;
      let release!: () => void;
      queueRef.current = new Promise<void>((resolve) => {
        release = resolve;
      });
      await runAfter;

      try {
        const cached = urlBySsmlRef.current.get(ssml);
        if (cached !== undefined) {
          // 同一 SSML は再合成しない (二重課金の防止)。
          setAudioStates((prev) => ({
            ...prev,
            [key]: { status: 'ready', url: cached, error: null },
          }));
          return true;
        }

        setAudioStates((prev) => ({
          ...prev,
          [key]: { status: 'synthesizing', url: null, error: null },
        }));

        const response = await previewAudio({
          ssml,
          // Provider / Voice は選択させない (要件で Polly / Takumi 固定)。
          voiceId: SSML_TUNING_VOICE_ID,
          language: SSML_TUNING_LANGUAGE_KEY,
        });
        const url = base64ToAudioUrl(response.content_base64, response.content_type);
        if (!mountedRef.current) {
          // アンマウント後に届いた音声は表示先が無いため、即座に解放して捨てる。
          URL.revokeObjectURL(url);
          return false;
        }
        urlBySsmlRef.current.set(ssml, url);
        createdUrlsRef.current.add(url);
        setAudioStates((prev) => ({
          ...prev,
          [key]: { status: 'ready', url, error: null },
        }));
        return true;
      } catch (e) {
        /*
         * ⚠️ 例外を外へ投げない。
         * 一括生成のループが 1 件の失敗で止まると、残りの候補の音声が
         * 作られず「1 件失敗したら他も聴けない」ことになる (計画 8.6.2)。
         * サーバのメッセージ (SSML 構文エラーの 400 など) はそのまま保持し、
         * 候補カードに表示して再試行の判断材料にする。
         */
        setAudioStates((prev) => ({
          ...prev,
          [key]: {
            status: 'error',
            url: null,
            error: describeApiErrorMessage(e, '音声の生成に失敗しました'),
          },
        }));
        return false;
      } finally {
        release();
      }
    },
    [previewAudio],
  );

  return { audioStates, synthesize, markPending, reset };
}
